import { observer } from 'mobx-react'
import { useCallback, useEffect } from 'react'

import { useStudentAppMediaQuery } from '~/@common/hooks/useMediaQuery'
import { useRepository } from '~/@common/hooks/useRepository'
import { errorHandlerService } from '~/@common/services'
import { mixpanelClient } from '~/@common/utils/trackings/mixpanel'

import { useMobilePagedStudyList } from '../../@hooks/useMobilePagedStudyList'
import { type RecentStudyType, recentStudyTypeMenuText } from '../../@common/types'
import RecentStudyService from '../../@service/RecentStudy.service'
import StudyListView from '../StudyList/StudyListView'

/**
 * 최근 학습 섹션 (학생) — Container.
 * RecentStudyService 접근 / 초기 로드 / 서버 페이지네이션 / 모바일 더보기·접기 /
 * 모달·트래킹 등 동작성 로직을 담당하고, 시각 표현은 StudyListView 에 위임한다.
 */
const RecentStudySection = () => {
  const service = useRepository(RecentStudyService)
  const { recentStudies, recentStudyType, moreRecentStudyParams, isLastPage, isReady } = service
  const { isMobile } = useStudentAppMediaQuery()
  const allItems = recentStudies ?? []

  // 초기 로드 (탭 변경 시 서비스가 recentStudyType 을 갱신하면 재로드)
  const loadRecentStudies = useCallback(async () => {
    try {
      await service.loadRecentStudies(recentStudyType)
    } catch (err) {
      errorHandlerService.handle(err)
    }
  }, [service, recentStudyType])

  useEffect(() => {
    void loadRecentStudies()
  }, [loadRecentStudies])

  // 서버 페이지네이션 추가 로드 (성공 여부 반환 — 실패 시 page 증가 방지)
  const loadMore = useCallback(async (): Promise<boolean> => {
    if (!moreRecentStudyParams || isLastPage) return true
    try {
      await service.loadMoreRecentStudies()
      return true
    } catch (err) {
      errorHandlerService.handle(err)
      return false
    }
  }, [moreRecentStudyParams, isLastPage, service])

  const { visibleItems, hasMobileMore, shouldShowMobileButton, handleMobileToggle } =
    useMobilePagedStudyList({
      items: allItems,
      isMobile,
      hasServerMore: !isLastPage,
      loadMore,
    })

  const handleTabChange = useCallback(
    (type: RecentStudyType) => {
      if (!isReady) return
      service.setRecentStudyType(type)
      mixpanelClient.track('학습홈_학습지리스트탭_Click', {
        탭_이름: recentStudyTypeMenuText[type],
      })
    },
    [isReady, service],
  )

  return (
    <StudyListView
      items={visibleItems}
      activeType={recentStudyType}
      onChangeType={handleTabChange}
      hasMobileMore={shouldShowMobileButton ? hasMobileMore : undefined}
      onMobileLoadMore={() => void handleMobileToggle()}
      onReachEnd={() => void loadMore()}
      onRefresh={loadRecentStudies}
      isReady={isReady}
    />
  )
}

export default observer(RecentStudySection)
